import { BufferCleaner } from '@bufferController/BufferCleaner/BufferCleaner';
import { BufferOpsQueue, TBufferOperation } from '@bufferController/BufferOpsQueue/BufferOpsQueue';
import { BufferStallsDetector } from '@bufferController/BufferStallsDetector/BufferStallsDetector';
import { GapController } from '@bufferController/GapController/GapController';
import { PlaybackSpeedController } from '@bufferController/PlaybackSpeedController/PlaybackSpeedController';
import { PlaybackStateController } from '@bufferController/PlaybackStateController/PlaybackStateController';
import { ConfigConsumer } from '@configProvider/ConfigConsumer';
import { EDebuggableModuleState, IDebbugable, TDebuggableAdditionalInfo } from '@customTypes/common';
import { TEventBus } from '@eventBus/EventBus';
import { Fragment } from '@fragment/Fragment';
import { FragmentParserEvents } from '@fragmentParser/FragmentParser.events';
import { Logger, TLogger } from '@logger/Logger';
import { ResolutionsManagerEvents } from '@resolutionsManager/ResolutionsManager.events';
import { StreamLoaderModuleEvents } from '@streamLoader/StreamLoader.events';
import bind from '@utils/decorators/bind';
import { IHydratedManifest } from '@utils/m3u8Hydrator/m3u8Hydrator';

import { BufferChunksCache } from './BufferChunksCache/BufferChunksCache';
import { IBufferControllerConfig } from './BufferController.config';
import { BufferControllerEvents } from './BufferController.events';
import { BufferMetrics } from './BufferMetrics/BufferMetrics';

const getMimeTypeByCodecs: (codecs: string) => string = (codecs) => `video/mp4; codecs="${codecs}"`;
export class BufferController extends ConfigConsumer<IBufferControllerConfig> implements IDebbugable {
  public _eventBus: TEventBus;
  public state: EDebuggableModuleState = EDebuggableModuleState.OK;
  public status: string = 'Idle';
  public additionalInfo: TDebuggableAdditionalInfo = {};
  public groups: string[] = ['buffer', 'general'];

  private _sourceBuffer: SourceBuffer | null = null;
  private _isSourceBufferReady: boolean = false;
  private _awaitingForNextSourceBuffer: boolean = false;
  private _isVODMode: boolean = false;
  private _closeOnNextSegment: boolean = false;
  private _currentVideoDuration: number = Number.POSITIVE_INFINITY;
  private _videoElement: HTMLVideoElement | null = null;
  private _mediaSource: MediaSource | null = null;
  private _knownObjectURL: string = '';
  private _mimeType: string = 'video/mp4; codecs="mp4a.40.2,avc1.64001f"';
  private _shouldRecreateSourceBufferOnStall: boolean = false;
  private _sourceBufferStuckTimer: ReturnType<typeof setTimeout> | null = null;

  private readonly _onFragmentParsedListener: string;
  private readonly _onSoftResetRequestedListener: string;
  private readonly _onSourceBufferOperationRequestedListener: string;
  private readonly _onVODPlaylistDetectedListener: string;
  private readonly _onSimplePlaylistDetectedListener: string;
  private readonly _onLLPlaylistDetectedListener: string;
  private readonly _onPlaylistInfoAvailableListener: string;
  private readonly _onResolutionCodecChangeRequestListener: string;
  private readonly _onResolutionChangeListener: string;
  private readonly _onPlaybackStallChangedListener: string;
  private readonly _onSourceBufferStuckListener: string;
  private readonly _bufferMetrics: BufferMetrics;
  private readonly _gapController: GapController;
  private readonly _bufferStallsDetector: BufferStallsDetector;
  private readonly _bufferOpsQueue: BufferOpsQueue;
  private readonly _bufferCleaner: BufferCleaner;
  private readonly _playbackSpeedController: PlaybackSpeedController;
  private readonly _playbackStateController: PlaybackStateController;
  private readonly _bufferChunksCache: BufferChunksCache;
  private readonly _onEndOfStreamReachedListener: string;
  private readonly _logger: TLogger;

  constructor(eventBus: TEventBus) {
    super('bufferController', eventBus);
    this._logger = Logger(eventBus, 'Doppio - BufferController');

    this._eventBus = eventBus;

    this._logger = Logger(this._eventBus, 'Doppio - BufferController');
    this._logger.info('Created');

    this._bufferCleaner = new BufferCleaner(this._eventBus);
    this._bufferMetrics = new BufferMetrics(this._eventBus);
    this._bufferStallsDetector = new BufferStallsDetector(this._eventBus);
    this._bufferOpsQueue = new BufferOpsQueue(this._eventBus);
    this._playbackSpeedController = new PlaybackSpeedController(this._eventBus);
    this._playbackStateController = new PlaybackStateController(this._eventBus);
    this._bufferChunksCache = new BufferChunksCache(this._eventBus);

    this._onFragmentParsedListener = this._eventBus.subscribe(FragmentParserEvents.fragments.fragmentsParsed, this._onFragmentsParsed);
    this._onSoftResetRequestedListener = this._eventBus.subscribe(
      BufferControllerEvents.requestResetWithOverplay,
      this._onSoftResetRequested,
    );
    this._onVODPlaylistDetectedListener = this._eventBus.subscribe(
      StreamLoaderModuleEvents.playlistRotator.playlistType.vodDetected,
      this._onVODPlaylistDetected,
    );
    this._onSimplePlaylistDetectedListener = this._eventBus.subscribe(
      StreamLoaderModuleEvents.playlistRotator.playlistType.simpleDetected,
      this._onLivePlaylistDetected,
    );
    this._onLLPlaylistDetectedListener = this._eventBus.subscribe(
      StreamLoaderModuleEvents.playlistRotator.playlistType.llDetected,
      this._onLivePlaylistDetected,
    );

    this._onSourceBufferOperationRequestedListener = this._eventBus.subscribe(
      BufferControllerEvents.requestSourceBufferOperation,
      this._onSourceBufferOperationRequested,
    );

    this._onPlaylistInfoAvailableListener = this._eventBus.subscribe(
      StreamLoaderModuleEvents.playlistRotator.playlists.playlistInfo,
      this._onPlaylistInfoAvailable,
    );

    this._onEndOfStreamReachedListener = this._eventBus.subscribe(BufferControllerEvents.endOfStreamReached, this._onEndOfStreamReached);

    this._onResolutionCodecChangeRequestListener = this._eventBus.subscribe(
      BufferControllerEvents.changeCodec,
      this._onResolutionCodecChangeRequest,
    );

    this._onResolutionChangeListener = this._eventBus.subscribe(ResolutionsManagerEvents.resolutionChanged, this._onResolutionChange);
    this._onPlaybackStallChangedListener = this._eventBus.subscribe(
      BufferControllerEvents.playbackStallChanged,
      this._onPlaybackStallChanged,
    );
    this._onSourceBufferStuckListener = this._eventBus.subscribe(BufferControllerEvents.sourceBufferStuck, this._onSourceBufferStuck);

    this._gapController = new GapController(this._eventBus);
  }

  public attachVideoElement(videoElement: HTMLVideoElement): void {
    this._logger.info('Attach video element', videoElement);
    this._bufferMetrics.setVideoElement(videoElement);
    this._playbackSpeedController.setVideoElement(videoElement);
    this._gapController.setVideoElement(videoElement);
    this._bufferCleaner.setVideoElement(videoElement);
    this._playbackStateController.setVideoElement(videoElement);

    this._videoElement = videoElement;
    this._recreateMediaSource();
  }

  public scheduleSourceBufferRecreation(): void {
    this._awaitingForNextSourceBuffer = true;
  }

  public detachVideoElement(): void {
    this._logger.info('Detaching video element');

    this._eventBus.publish(BufferControllerEvents.requestResetWithOverplay, {});
    this._bufferMetrics.removeVideoElement();
    this._playbackSpeedController.removeVideoElement();
    this._gapController.removeVideoElement();
    this._bufferCleaner.removeVideoElement();
    this._playbackStateController.setVideoElement(null);

    if (
      this._mediaSource?.readyState === 'open' &&
      this._videoElement &&
      this._videoElement.readyState >= this._videoElement.HAVE_METADATA
    ) {
      try {
        this._logger.verbose('Calling endOfStream of mediaSource');
        // endOfStream could trigger exception if any sourcebuffer is in updating state
        // we don't really care about checking sourcebuffer state here,
        // as we are anyway detaching the MediaSource
        // let's just avoid this exception to propagate
        this._mediaSource.endOfStream();
      } catch (err) {
        this._logger.warn(`Error occurred while detaching video element. Ignoring it ${(err as Error).message}`);
      }
    }

    try {
      if (this._mediaSource && this._sourceBuffer) {
        if ([...this._mediaSource.sourceBuffers].includes(this._sourceBuffer)) {
          this._logger.debug('Removing sourceBuffer from MediaSource');
          this._mediaSource.removeSourceBuffer(this._sourceBuffer);
        }
        this._sourceBuffer = null;
      }
    } catch (err) {
      this._logger.error(`Failed to reset source buffer ${(err as Error).message}`);
    }

    if (this._mediaSource) {
      this._mediaSource.removeEventListener('sourceopen', this._onSourceBufferOpened);
      this._mediaSource.removeEventListener('sourceclose', this._onSourceBufferClosed);
      this._mediaSource.removeEventListener('error', this._onSourceBufferError);
    }

    if (this._videoElement) {
      if (this._knownObjectURL) {
        const prevObjectUrl: string = this._knownObjectURL;
        setTimeout(() => {
          this._logger.debug(`Revoking Object URL ${prevObjectUrl}`);
          globalThis.URL.revokeObjectURL(prevObjectUrl);
        }, 100);
      }

      if (this._videoElement.src === this._knownObjectURL) {
        this._videoElement.removeAttribute('src');
        this._videoElement.load();
      }
    }

    this._mediaSource = null;
    this._videoElement = null;
    this._knownObjectURL = '';
    this._isSourceBufferReady = false;
  }

  public destroy(): void {
    super.destroy();

    this._logger.info('Destroy');
    this.detachVideoElement();

    this._eventBus.unsubscribe(this._onFragmentParsedListener);
    this._eventBus.unsubscribe(this._onSoftResetRequestedListener);
    this._eventBus.unsubscribe(this._onSourceBufferOperationRequestedListener);
    this._eventBus.unsubscribe(this._onVODPlaylistDetectedListener);
    this._eventBus.unsubscribe(this._onSimplePlaylistDetectedListener);
    this._eventBus.unsubscribe(this._onLLPlaylistDetectedListener);
    this._eventBus.unsubscribe(this._onPlaylistInfoAvailableListener);
    this._eventBus.unsubscribe(this._onEndOfStreamReachedListener);
    this._eventBus.unsubscribe(this._onResolutionCodecChangeRequestListener);
    this._eventBus.unsubscribe(this._onResolutionChangeListener);
    this._eventBus.unsubscribe(this._onPlaybackStallChangedListener);
    this._eventBus.unsubscribe(this._onSourceBufferStuckListener);

    this._gapController.destroy();
    this._bufferMetrics.destroy();
    this._bufferOpsQueue.destroy();
    this._bufferCleaner.destroy();
    this._playbackSpeedController.destroy();
    this._playbackStateController.destroy();
    this._bufferChunksCache.destroy();

    this._disarmSourceBufferStuckTimer();
  }

  public reset(): void {
    this._disarmSourceBufferStuckTimer();

    this._recreateSourceBuffer();
    this._bufferChunksCache.reset();
  }

  @bind
  private _onFragmentsParsed(fragments: Fragment[]): void {
    if (this._awaitingForNextSourceBuffer) {
      this._awaitingForNextSourceBuffer = false;
      this._recreateSourceBuffer();
    }

    this._bufferOpsQueue.addBufferOperation(
      () => {
        if (this._mediaSource && this._mediaSource.readyState === 'open') {
          this._mediaSource.duration = this._currentVideoDuration;
        }
      },
      () => {
        this._logger.warn('Could not set mediaSource duration');
      },
      'setMediaSourceDuration',
    );

    //use for instead of forEach to maintain order of fragments
    for (const fragment of fragments) {
      this._bufferOpsQueue.addBufferOperation(
        (buffer: SourceBuffer) => {
          this._logger.debug(`Adding ${fragment.getMsnPn()} to buffer`);

          buffer.appendBuffer(fragment.data as ArrayBuffer);

          if (fragment.msn > -1) {
            this._logger.debug(`${fragment.startTs} -> ${fragment.endTs} loaded (${fragment.getMsnPn()})`);
            this._eventBus.publish(BufferControllerEvents.fragmentAdded, fragment);
          }

          if (this._mediaSource && fragment.endTs === this._mediaSource.duration) {
            this._eventBus.publish(BufferControllerEvents.endOfStreamReached, {});
          }
        },
        (buffer: SourceBuffer, error: Error) => {
          this._logger.error(`${fragment.url} (${fragment.getMsnPn()}) cannot be added to buffer: ${error}`);
        },
        'appendBuffer',
      );
    }

    if (!this._sourceBuffer) {
      this._bufferChunksCache.append(fragments);
    }

    this._bufferOpsQueue.addBufferOperation(
      () => {
        if (this._mediaSource && this._mediaSource.readyState === 'open') {
          this._mediaSource.duration = this._currentVideoDuration;

          if (
            this._videoElement &&
            this._videoElement.currentTime === 0 &&
            this._videoElement.buffered.length > 0 &&
            this._videoElement.buffered.start(0) > 0
          ) {
            this._videoElement.currentTime = this._videoElement.buffered.start(0);
          }
        }
      },
      () => {
        this._logger.warn('Could not change current time of video');
      },
      'setVideoElementStartPosition',
    );
  }

  @bind
  private _onSourceBufferOpened(): void {
    this._logger.debug('Source buffer is open now');
    this._disarmSourceBufferStuckTimer();

    if (this._mediaSource) {
      this._logger.debug('Media source known');
      if (this._mediaSource.readyState === 'open') {
        this._logger.debug('State of mediaSource is open. Setting duration');
        this._mediaSource.duration = this._currentVideoDuration;
      }

      this._isSourceBufferReady = true;

      this._sourceBuffer = this._mediaSource.addSourceBuffer(this._mimeType);

      this._logger.debug(`Source buffer is open with mimeType ${this._mimeType}`);

      this._bufferOpsQueue.setSourceBuffer(this._sourceBuffer);

      const cache: Fragment[] = this._bufferChunksCache.getAll();
      this._bufferChunksCache.destroy();
      if (cache.length) {
        this._onFragmentsParsed(cache);
      }
    }
  }

  private _recreateSourceBuffer(): void {
    this._logger.info('Will recreate source buffer');
    this._bufferOpsQueue.immediatelyRecreateSourceBuffer(this._mediaSource);

    if (this._videoElement) {
      this._videoElement.currentTime = 0;
    }
  }

  @bind
  private _onResolutionCodecChangeRequest({ codec }: { codec: string }): void {
    if (codec) {
      const mimeType: string = getMimeTypeByCodecs(codec);

      if (mimeType !== this._mimeType) {
        this._mimeType = mimeType;
      }
    }
  }

  @bind
  private _onSourceBufferClosed(): void {
    this._logger.debug('Source buffer closed');
    this._isSourceBufferReady = false;
  }

  @bind
  private _onSourceBufferError(): void {
    this._logger.debug('Source buffer error');
    this._isSourceBufferReady = false;
  }

  @bind
  private _onSoftResetRequested(): void {
    this._logger.debug('Soft reset requested');
    this._shouldRecreateSourceBufferOnStall = false;
    this._awaitingForNextSourceBuffer = true;
    this._playbackStateController.resetPlayingTimeCollector();
  }

  @bind
  private _endOfStreamReached(): void {
    if (this._mediaSource) {
      this._closeOnNextSegment = true;
    }
  }

  @bind
  private _onSourceBufferOperationRequested(operation: TBufferOperation): void {
    this._bufferOpsQueue.addBufferOperation(operation);
  }

  @bind
  private _onLivePlaylistDetected(): void {
    this._isVODMode = false;
    this._bufferCleaner.setVODMode(false);
    this._gapController.setIsVod(false);
    this._currentVideoDuration = Number.POSITIVE_INFINITY;
    this._playbackSpeedController.resume();

    if (this._mediaSource && this._mediaSource.readyState === 'open') {
      this._mediaSource.duration = this._currentVideoDuration;
    }
  }

  @bind
  private _onVODPlaylistDetected(): void {
    this._isVODMode = true;
    this._bufferCleaner.setVODMode(true);
    this._playbackSpeedController.pause();
    this._gapController.setIsVod(true);
  }

  @bind
  private _onPlaybackStallChanged(isStalled: boolean): void {
    if (isStalled && this._shouldRecreateSourceBufferOnStall) {
      this._logger.info('Will recreate source buffer, because playback stall was detected');
      this._onSoftResetRequested();
    }
  }

  @bind
  private _onPlaylistInfoAvailable(playlist: IHydratedManifest): void {
    if (playlist && playlist.playlists && playlist.playlists[0].attributes.CODECS) {
      this._mimeType = getMimeTypeByCodecs(playlist.playlists[0].attributes.CODECS);
      this._bufferOpsQueue.addBufferOperation(
        (buffer) => {
          buffer.changeType(this._mimeType);
        },
        () => {
          this._logger.warn('Could not change codecs');
        },
        'changeType',
      );
    }

    if (playlist.playlistType !== 'VOD' && !playlist.endList) {
      return;
    }

    this._currentVideoDuration = playlist.segments.reduce((s, c) => s + c.duration, 0);

    if (this._mediaSource && this._mediaSource.readyState === 'open') {
      this._mediaSource.duration = this._currentVideoDuration;
      this._mediaSource.setLiveSeekableRange(0, this._currentVideoDuration);
    }
  }

  @bind
  private _onEndOfStreamReached(): void {
    this._bufferOpsQueue.addBufferOperation(() => {
      this._logger.info('Setting endOfStream');
      this._mediaSource?.endOfStream();
    });
  }

  @bind
  private _onResolutionChange(): void {
    if (this.config.recreateSourceBufferOnResolutionChange) {
      this._logger.info(
        'Will recreate source buffer, because resolution was changed, and config.recreateSourceBufferOnResolutionChange is true',
      );
      this._onSoftResetRequested();
    } else {
      this._shouldRecreateSourceBufferOnStall = true;
    }
  }

  @bind
  private _onSourceBufferStuck(): void {
    this._logger.info('Source buffer stuck. Schedule reset');
    this._onSoftResetRequested();
  }

  @bind
  private _recreateMediaSource(): void {
    this._logger.info('Recreate media source');
    if (this._mediaSource) {
      this._logger.debug('Media source already known. Removing event listeners');
      this._mediaSource.removeEventListener('sourceopen', this._onSourceBufferOpened);
      this._mediaSource.removeEventListener('sourceclose', this._onSourceBufferClosed);
      this._mediaSource.removeEventListener('error', this._onSourceBufferError);
    }

    this._mediaSource = new BufferController.MediaSource();
    this._logger.debug('Media source created. Attaching event listeners');
    this._mediaSource.addEventListener('sourceopen', this._onSourceBufferOpened, { once: true });
    this._mediaSource.addEventListener('sourceclose', this._onSourceBufferClosed);
    this._mediaSource.addEventListener('error', this._onSourceBufferError);

    if (this._videoElement) {
      this._videoElement.src = globalThis.URL.createObjectURL(this._mediaSource);
      this._logger.debug(`MSE URL: ${this._videoElement.src}`);
    }

    this._sourceBufferStuckTimer = setTimeout(() => {
      this._logger.debug('Source buffer stuck timeout set');
      this._recreateMediaSource();
    }, this.config.sourceBufferStuckTimeout);
  }

  private _disarmSourceBufferStuckTimer(): void {
    if (this._sourceBufferStuckTimer) {
      this._logger.debug('Source buffer stuck timer disarm');
      clearTimeout(this._sourceBufferStuckTimer);
    }
  }

  get additionalModules(): Record<string, IDebbugable> {
    return {
      gapController: this._gapController,
      bufferMetrics: this._bufferMetrics,
      playbackSpeed: this._playbackSpeedController,
    };
  }

  static get MediaSource(): typeof MediaSource {
    if (window?.ManagedMediaSource) {
      return window.ManagedMediaSource;
    }

    if (window?.MediaSource) {
      return window.MediaSource;
    }

    throw new Error('No Media Source candidates found');
  }
}
